import type { FC } from "react";
import { Button, Flex, Spacing, Text, useTheme } from "@huub-web/ui";
import { Illustration } from "@huub-web/shared";
import { useTranslation } from "react-i18next";

import { slideContainerStyle } from "../AuthScreens.css";
import type { BaseSlideProps } from "../../../types";

/**
 * Map of Auth0 English messages to i18n translation keys.
 * Auth0 sends English messages in redirect URLs; we map them to i18n keys
 * so the component displays the correct locale.
 */
const AUTH0_MESSAGE_KEY_MAP: Record<string, string> = {
  "your email was verified. you can continue using the application.":
    "verificationResult.messages.emailVerified",
  "this url can be used only once": "verificationResult.messages.linkUsed",
  "access expired": "verificationResult.messages.linkExpired",
  "the link has expired": "verificationResult.messages.linkExpired",
  "user already verified": "verificationResult.messages.alreadyVerified",
};

/**
 * Map an Auth0 English message to an i18n key.
 * Returns null if no mapping is found (the raw message will be shown as-is).
 */
const getMessageI18nKey = (message: string | null): string | null => {
  if (!message) {
    return null;
  }

  const lowerMessage = message.toLowerCase();

  // Check for exact match (case-insensitive)
  if (AUTH0_MESSAGE_KEY_MAP[lowerMessage]) {
    return AUTH0_MESSAGE_KEY_MAP[lowerMessage];
  }

  // Check for partial match
  for (const [pattern, i18nKey] of Object.entries(AUTH0_MESSAGE_KEY_MAP)) {
    if (lowerMessage.includes(pattern)) {
      return i18nKey;
    }
  }

  return null;
};

export type VerificationResultSlideProps = BaseSlideProps & {
  success: boolean;
  message: string | null;
  onContinue: () => void;
};

/**
 * VerificationResultSlide component for displaying status messages.
 * Shows success or error state for verification, logout, or other actions.
 */
export const VerificationResultSlide: FC<VerificationResultSlideProps> = ({
  leading,
  trailing,
  success,
  message,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const hasIllustration = theme === "light" || theme === "dark";

  const messageI18nKey = getMessageI18nKey(message);
  const displayMessage =
    messageI18nKey !== null
      ? t(messageI18nKey)
      : (message ??
        t(
          `verificationResult.defaultMessages.${success ? "success" : "error"}`,
        ));

  const lowerMessage = message?.toLowerCase() ?? "";
  const isLoggedOut =
    lowerMessage.includes("uitgelogd") || lowerMessage.includes("logged out");

  const title = success
    ? isLoggedOut
      ? t("verificationResult.titles.goodbye")
      : t("verificationResult.titles.verified")
    : t("verificationResult.titles.error");

  return (
    <>
      {leading}
      <Flex
        className={slideContainerStyle}
        direction="column"
        gap="m"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="s" align="center">
          {hasIllustration ? (
            <Illustration name={success ? "default" : "shrug"} />
          ) : (
            <Spacing height="s" />
          )}
          <Text as="h2" variant="displayM" bold align="center">
            {title}
          </Text>
          <Text as="p" align="center">
            {displayMessage}
          </Text>
        </Flex>

        <Button variant="primary" onClick={onContinue}>
          {success
            ? t("verificationResult.buttons.login")
            : t("verificationResult.buttons.tryAgain")}
        </Button>

        <Spacing height="m" />

        {trailing}
      </Flex>
    </>
  );
};
